import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, MessageCircle, Send, Sparkles, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Which tour fits a family with kids?",
  "Best time to visit Zermatt?",
  "How do airport transfers work?",
];

const getSessionId = () => {
  let id = localStorage.getItem("voyage_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("voyage_chat_session", id);
  }
  return id;
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line || line === "[DONE]") continue;
          try {
            const payload = JSON.parse(line);
            if (payload.t) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + payload.t };
                return copy;
              });
            } else if (payload.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: payload.error };
                return copy;
              });
            }
          } catch {
            /* partial chunk */
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "Sorry, I'm having trouble connecting right now. Please try again in a moment.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating bubble */}
      <motion.button
        data-testid="chat-bubble-button"
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-5 right-5 z-[150] w-14 h-14 rounded-full bg-[var(--brand)] text-white flex items-center justify-center shadow-xl shadow-[var(--brand)]/40"
        aria-label={open ? "Close travel assistant" : "Open travel assistant"}
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="fixed bottom-[84px] right-5 z-[150] w-[calc(100vw-40px)] max-w-[400px] h-[540px] max-h-[70vh] rounded-3xl bg-white border border-slate-100 shadow-2xl flex flex-col overflow-hidden"
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="bg-[#0b132b] px-5 py-4 flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-[var(--brand)] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </span>
              <div>
                <p className="font-display font-bold text-white text-sm leading-tight">Voyage Assistant</p>
                <p className="text-[11px] text-slate-300 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" /> Online — answers in your language
                </p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#f8fafc]" data-testid="chat-messages">
              {messages.length === 0 && (
                <div>
                  <div className="rounded-2xl rounded-tl-md bg-white border border-slate-100 px-4 py-3 text-sm text-slate-600 shadow-sm max-w-[85%]" data-testid="chat-welcome">
                    Hello! I'm Nora, your Swiss travel assistant. Ask me about our tours, the best seasons, airport transfers — anything Switzerland.
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {SUGGESTIONS.map((s, i) => (
                      <button
                        key={i}
                        data-testid={`chat-suggestion-${i}`}
                        onClick={() => send(s)}
                        className="rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-bold text-slate-600 hover:border-[var(--brand)] hover:text-[var(--brand)] transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((msg, i) => (
                <div
                  key={i}
                  dir="auto"
                  data-testid={`chat-message-${msg.role}-${i}`}
                  className={`px-4 py-3 text-sm leading-relaxed max-w-[85%] whitespace-pre-wrap shadow-sm ${
                    msg.role === "user"
                      ? "ml-auto rounded-2xl rounded-br-md bg-[var(--brand)] text-white"
                      : "rounded-2xl rounded-tl-md bg-white border border-slate-100 text-slate-700"
                  }`}
                >
                  {msg.text || (busy && msg.role === "assistant" ? <Loader2 className="w-4 h-4 animate-spin text-[var(--brand)]" /> : "")}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="p-3 border-t border-slate-100 bg-white flex items-center gap-2"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about tours, seasons, transfers…"
                className="flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-sm font-medium outline-none focus:ring-2 focus:ring-[var(--brand)]/30 focus:border-[var(--brand)] transition-all"
              />
              <button
                type="submit"
                data-testid="chat-send-button"
                disabled={busy || !input.trim()}
                className="w-10 h-10 shrink-0 rounded-full bg-[var(--brand)] text-white flex items-center justify-center shadow-lg shadow-[var(--brand)]/30 transition-colors hover:bg-[var(--brand-dark)] disabled:opacity-50"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
